import { TableCellProps } from "@mui/material";
import { Dayjs } from "dayjs";
import React from "react";

export type UserType = string | null;
export type AuthenticateType = (user: string | null) => void;
export type SetUserType = React.Dispatch<UserType>;

export type SingleExpertDataType = {
    Age: number,
    consent: boolean,
    cvYear: string,
    cvlink: string,
    donors: string,
    email: string,
    exportID: number,
    keywords: string,
    languages: string,
    name: string,
    nationality: string,
    regions: string,
    referencesWorkedCount?: number,
    referencesViaPartnerCount?: number,
    viaPartners?: string | null,
    positions?: string,
    // Base64-encoded photo (same shape as Partner's profile.logo), returned by the backend
    // on expert list/detail responses once uploaded. Absent/null -> render a placeholder.
    photo?: string | null,
    // Short AI-generated (or manually written) professional summary, manually (re)generated
    // via the "Generate with AI" button on the expert form - never auto-generated on save.
    paragraph?: string | null,
    [key: string]: any;
};
export type ExpertDataArrayType = SingleExpertDataType[] | never[];
export type SetExpertDataArrayType = React.Dispatch<ExpertDataArrayType>;

export type PageType = number;
export type SetPageType = React.Dispatch<number>;

export type LoadingType = boolean;
export type SetLoadingType = React.Dispatch<boolean>;

export type FiltersType = {
    [key: string]: any;
    names: string[];
    languages: string[];
    regions: string[];
    donors: string[];
    keywords: any; // not sure what keywords type is
    fulltext: string[];
    positions?: string[];
    // Worked on an INTEGRATION project: '' = not filtered, 'any' = direct or via partner,
    // 'integration' = contracted by INTEGRATION, 'partner' = only via a partner.
    integrationProject?: '' | 'any' | 'integration' | 'partner';
    // ID of a reference (ReferenceDatabase.ID) picked via the references/search typeahead;
    // filters the experts list down to exactly the experts linked to that reference.
    referenceId?: number | '';
}

// A single reference search result, returned by GET api/references/search and used by the
// "Search by Project worked with" experts filter to resolve a reference to its ID.
export type ReferenceSearchOptionType = {
    id: number;
    projectNo: string;
    label: string; // "ProjectNo - Title", ready-made from reference_list_vw.ProjNumTitle
};

// A single reference an expert has worked on, shown on the Expert detail view's
// "Project History" tab.
export type ExpertProjectHistoryType = {
    projectNo: string;
    projectTitle: string;
    position: string;
    daysWorked: number | null;
    ratePerDay: number | null;
    source?: 'Integration' | 'Partner' | null;
    partnerID?: number | null;
    partnerName?: string | null;
};

// A single expert linked to a reference, including the per-assignment attributes
// recorded in bridge_experts_references.
export type LinkedExpertType = {
    id: number; // bridge_experts_references.ID
    expertID: number;
    name: string;
    email: string;
    position: string;
    ratePerDay: number | null;
    daysWorked: number | null;
    source: string;
    partnerID?: number | null;
    partnerName?: string | null;
    cvlink: string | null;
    cvYear: string | boolean | null;
};

// An expert not yet linked to a given reference, used to populate the preselection
// dropdown when adding an expert to a reference.
export type AvailableExpertType = {
    id: number; // expertID, used as the preselection value field
    expertID: number;
    name: string;
    email: string;
};

// A single partner linked to a reference. Deliberately minimal - unlike LinkedExpertType,
// this does NOT carry input_share_partner/profit_share_partner/comment; those stay editable
// only from the Partner-side collaboration form (see PartnerCollaborationService).
export type LinkedPartnerType = {
    id: number; // bridge_partners_references.ID
    partnerID: number;
    name: string;
    email: string;
    city: string;
    comment?: string; // role of the partner on this reference, e.g. "JV partner"
};

// A partner not yet linked to a given reference, used to populate the preselection
// search-as-you-type picker when adding a partner to a reference.
export type AvailablePartnerType = {
    id: number; // partnerID, used as the preselection value field
    partnerID: number;
    name: string;
    email: string;
};

export type ReferencesTableType = {
    [key: string]: any;
    ProjNumTitle: string;
    ProjectNo: string;
    ProjectTitle: string ;
    Country: string;
    OriginOfFunding: string;
    Client_short: string;
    ProjectDescription_short: string;
    StartDate: Date | string;
    CompletionDate: Date | string;
    ProjectValueEuro: number;
    FieldOfService: string;
    INTEE: boolean;
    Homepage: boolean;   
    country_homepage: string;  
    references_version_count: number;
}

export type ReferencesFilterType = { 
    completionDate: Dayjs; 
    countries: string[];
    selectedCompany: string;
    projectValueEuro: number;
    fieldsOfServices: string[];
    refNumTitles: string[];
    fullTextProjectNumberList: string[];
    printSelectionList: string[];
}

export type ReferencesMultiFiltersType = {
    countries: string[],
    fieldsOfService: string[],
    refNumTitles: string[],
}

export type ReferenceDetailType = {
    ProjectID?: number;
    LangVerID?: number;
    ProjectNo: string;
    FocusOn?: string;
    ProjectTitle: string;
    ProjectTitle_short?: string;
    Country: string;
    Location?: string;
    Region?: string;
    short: string;
    FieldOfService: string;
    country_homepage?: string;
    OriginOfFunding: string;
    StartDay?: string;
    StartDate: Date | string;
    EndDay?: string;
    CompletionDate: Date | string;
    ProjectValue?: string;
    ProportionLegalEntity?: string;
    NoOfStaff: string;
    NoOfPersonMonth: string;
    Personnel: string;
    Personnel_EU_EOI: string;
    AssociatedFirms?: string;
    Consortium?: string;
    Lead: boolean | undefined;
    SeniorStaff: string;
    ProjectResults: string;
    ProjectBackground: string;
    ProjectObjectives: string;
    ProjectActivities?: string;
    ServicesProvided: string;
    ImplementationFeatures?: string;
    ProjectDescription_short?: string;
    extrashort_GTZ_PQ?: string;
    KeyWords?: string;
    Picture?: string;
    Category?: string;
    zuständig?: string;
    ClientContact: string;
    ProjectValueEuro: number | string;
    ProjectValueDollar: number | string;
    Folder?: string;
    NewRef?: boolean | undefined;
    INTEE: boolean | undefined;
    CoP?: boolean | undefined;
    variant_name?: string;
    variant_description?: string;
    ClientID?: number;
    Homepage?: boolean | undefined;
    Bemerkungen?: string;
    ConstructionServicesEUR?: number;
    ConstructionServicesDollar?: number;
    ProjectDescription_short_german?: string;
    address?: string;
    NoOfPersonMonthsByAssociatedFirms?: string;
    Textvariante1?: string;
    Textvariante2?: string;
    Textvariante3?: string;
    NoOfPersonMonthINT?: string;
    ProjectDescription_detailed?: string;
    extrashort_GTZ_PQ_german?: string;
}

// TypeScript array containing names of required fields for referece form
export const requiredFieldsReferenceForm: string[] = [
    "ProjectNo",
    "ProjectTitle",
    "Country",
    "short",
    "FieldOfService",
    "OriginOfFunding",
    "StartDate",
    "CompletionDate",
    "NoOfStaff",
    "NoOfPersonMonth",
    "Personnel",
    "Lead",
    "SeniorStaff",
    "ProjectBackground",
    "ProjectObjectives",
    "ServicesProvided",
    "ClientContact",
    "ProjectValueEuro",
    "ProjectValueDollar",
    "ProjectResults"
];


// TypeScript array containing names of required fields for expert form
export const requiredFieldsExpertsForm: string[] = [
    "firstName",
    "lastName",
    "email",
];

export type ReferenceAvailableOptionsType = {
    clients: string[],
    countries: string[],
    fieldsofServices: string[],
    countries_homepage: string[],
}

export type LangVerType = {
    ID: number;
    ProjectID: number;
    ProjectTitle: string;
    Country: string;
    Location: string;
    OriginOfFunding: string;
    NoOfStaff: string;
    NoOfPersonMonth: string;
    Personnel: string;
    Personnel_EU_EOI: string;
    SeniorStaff: string;
    ProjectDescription_detailed: string;
    ProjectResults: string;
    ProjectBackground: string;
    ProjectObjectives: string;
    ProjectActivities: string;
    ServicesProvided: string;
    ImplementationFeatures: string;
    ProjectDescription_short: string;
    extrashort_GTZ_PQ: string;
    LangID: number;
    TmStamp: number;
    Bemerkungen: string;
    Homepage: boolean;
    [key: string]: any;
}

export type VariantListType = {
    ProjectNo: string;
    name: string;
    variantID: number;
    selected: boolean;
}

export type TreeNode = {
    id: string;
    label: string;
    children: TreeNode[];
  };
  
export type MenuItemProps = {
    name: string;
    IconButton: React.ElementType;
    onClickFunction: () => void;
    adminOnly?: boolean;
  };

  export type CVDataType = {
    cvID?: number;
    cvFile: string;
    cvLanguage?: string;
    cvDate?: string;
    cvUploadedBy?: string;
    viewCV: string;
    cvDataType: string | undefined;
    isNew?: boolean;
  };

  export type CVFileBlobType = {
    blob : File,
    fileName: string,
}

export type ReferenceTableColumn = {
  key: string;
  label: string;
  width?: string;
  cellProps?: TableCellProps;
  render?: (item: ReferencesTableType, context: any) => React.ReactNode;
};

export type ExpertsTableColumn = {
  key: string;
  label: string;
  width?: string;
  sortable?: boolean;
  cellProps?: TableCellProps;
  render?: (item: any) => React.ReactNode;
};

  export type PartnersTableDto = {
    [key: string]: any;
    partnerId: number; // Unique identifier for the partner
    name: string;
    city: string;
    fos: string[]; // List of FieldOfService.strVal
    latest_collab_project_number: string | null; // ReferenceDatabase.ProjectNo
    latest_collab: string | null; // ReferenceDatabase.ProjectTitle_german
    latest_collab_date: Date | null; // ReferenceDatabase.CompletionDate
    number_of_collaborations: number;
    countries: string[];
}

export type PartnersMultiFiltersType = {
    countries: string[],
    fos: string[],
    names: string[],
}

export type PartnersFilterType = { 
    countries: string[];
    name: string[];
    fos: string[];
}

export type RegionListType = {
    countries: string[];
    adm1 : string[];
    adm2: string[];
    isFiltered: boolean;
}

export type TableColDefinition = {
    key: string;
    label: string;
    width: string;
    sortable?: boolean;
    isList?: boolean;
    openDetails?: boolean;
    icon?: React.ElementType;
}

export type CountryData = { country: string; region: string; continent: string };
export type RegionGroup = { key: string; group: string };

export type PartnerDetailsType = {
  contact: {
    name: string;
    short_name: string;
    headquarter: string;
    email: string;
    address: string;
    phone: string;
    contact_persons: {
      id: number;
      first_name: string;
      last_name: string;
      email: string;
      phone: string;
      position: string;
      comment: string;
    }[]
  };
  profile:{
      fos: string[]; // List of FieldOfService.strVal
      countries: string[];
      logo: string | null; // URL to the logo image
      folder: string | null; // URL to the company profile document
      remark: string | null; // Additional remarks or comments
      website: string | null; // URL to the partner's website
  };
  collab_history: {
    id: number; // Unique identifier for the collaboration
    project_number: string; // ReferenceDatabase.ProjectNo
    project_title: string; // ReferenceDatabase.ProjectTitle_german
    completion_date: Date | null; // ReferenceDatabase.CompletionDate
    lead: string; // ReferenceDatabase.Lead
    input_share_partner: string; 
    profit_share_partner: string; 
    comment: string; // ReferenceDatabase.Comment
  }[];
  experts: {
    id: string;
    name: string;
    position: string;
    rate: string; // Hourly rate or other relevant rate information
    comment: string; // Additional comments or notes about the expert
  }[];
  housekeeping: {
  created_at: Date; // Date when the partner was created
  created_by: string; // User who created the partner
  updated_at: Date; // Date when the partner was last updated
  updated_by: string; // User who last updated the partner
};
  fos_options?: string[]; // Optional field for additional FieldOfService options
  countries_options?: string[]; // Optional field for additional country options
  references_options?: string[]; // Optional field for additional reference options
}

export type PartnersAvailableOptions = {
    countries: string[];
    fos: string[];
}

export interface StructuredCriteria {
  key: string;
  value: string;
  icon: string;
}

export interface CriteriaValidationResult extends StructuredCriteria {
  requirementMet: boolean;     // True/false result of the validation
  comments: string[];          // List of evaluator comments (explanation why Met/Not Met)
  suggestions: string[];       // Improvements the expert can make
  scorePercent: number;        // Score contribution in %
  overallWeightPercent: number; // Weight of this criterion in overall evaluation in %
}

export interface Education {
  Institution: string;
  period: string;
  degree: string;
}

export interface Language {
  name: string;
  skill_level: string;
}

export interface Skill {
  point: string;
}

export interface RegionalExperience {
  country: string;
  period: string;
}

export interface TaskDescription {
  point: string;
}

export interface ProfessionalExperience {
  number: number;
  period: string;
  location: string;
  company: string;
  position: string;
  title: string;
  task_description: TaskDescription[];
}

export interface FormatedCV {
  family_name: string;
  first_name: string;
  date_of_birth: string;
  nationality: string;
  place_of_residence: string;
  civil_status: string;
  education: Education[];
  language: Language[];
  professional_body_membership: string;
  other_skills: Skill[];
  present_position: string;
  years_within_the_firm: string;
  regional_experience: RegionalExperience[];
  professional_experience: ProfessionalExperience[];
  publications_or_conferences: string;
}

